import is from '@/js/is'
import hasher from 'object-hash'
import deepEqual from 'deep-equal'
import pluckDeep from 'pluck-deep'
import dt from 'date-and-time'
import { getNearestAspectRatioID, getNearestFrameRateID, getMatchingDefinitionFormat,getRunBase } from '../shared/fidutils'
import { parseFullName } from 'parse-full-name'

// dont wany any settings dependencies in this namespace - everything should come as args
export default ((ns) => {

  ns.getRunIDFromDate = (date) => getRunBase(date)
  
  ns.formatDate = (d, f) => {
    const dp = dt.compile(f || 'MMM D YYYY');
    return dt.format(new Date(d), dp);
  }

  ns.splitFullName = ({ text }) => {
    return text ? parseFullName(text.toString()) : null
  }


  /**
   * strict object comparison
   * @param {object} a ob a
   * @param {object} b ob b
   * @return {boolean} the same?
   */
  ns.equals = (a, b) =>
    deepEqual(a, b, {
      strict: true,
    });

  // this will hoist the fields in a filmtoperson item
  // standarize them for form filling
  ns.makeTablePersonToFilmItem = (item) => {
    const cn = pluckDeep(item, 'Company.CompanyNames.name')
    const ob = {
      ...item,
      companyName: cn && cn[0],
      companyID: pluckDeep(item, 'Company.id'),
      relationshipTypeName: pluckDeep(item, 'FilmToPersonRelationshipType.RelationshipType.RelationshipTypeLocalized.name'),
      relationshipTypeID: pluckDeep(item, 'FilmToPersonRelationshipType.RelationshipType.id'),
      professionName: pluckDeep(item, 'Profession.ProfessionLocalized.name'),
      companyDepartmentName: pluckDeep(item, 'Profession.CompanyDepartment.CompanyDepartmentLocalized.name'),
      companyTypeName: ns.treeUntangle({
        item: pluckDeep(item, 'Profession.CompanyDepartment.CompanyType'),
        localizedKey: 'CompanyTypeLocalized',
        separator: '/'
      }),
      companyTypeID: pluckDeep(item, 'Profession.CompanyDepartment.CompanyType.id'),
      companyDepartmentID: pluckDeep(item, 'Profession.CompanyDepartment.id'),
      freelance: pluckDeep(item, 'Profession.freelance')
    }

    return ob
  };

  // a route change is triggered by clicking on a reason chip, or some other place
  // the reason encapsulates data about the that reason that can be used as a head start when routing to the
  // detail page for that matchtype - we just need to make it look like it came from a graphql search
  ns.routeChange = ({ router, reason, editing, hostID, hostType }) => {

    const routePackage = {
      name: reason.matchType,
      params: {
        id: reason.matchID,
        editing,
        hostID,
        hostType,
        item: {
          MatchReasons: [reason],
          itemType: reason.matchType,
          itemName: reason.match,
          itemID: reason.matchID
        }
      }
    }
    router.push(routePackage)
  }
  /**
   * create a column label for sheet address, starting at 1 = A, 27 = AA etc..
   * @param {number} columnNumber the column number
   * @return {string} the address label
   */
  ns.columnLabelMaker = (columnNumber, s) => {
    s =
      String.fromCharCode(((columnNumber - 1) % 26) + 'A'.charCodeAt(0)) +
      (s || '');
    return columnNumber > 26 ? ns.columnLabelMaker(Math.floor((columnNumber - 1) / 26), s) : s;
  };
  /**
   * generateUniqueString
   * get a unique string
   * @param {number} optAbcLength the length of the alphabetic prefix
   * @return {string} a unique string
   **/
  ns.generateUniqueString = (optAbcLength) => {
    const abcLength = isUndefined(optAbcLength) ? 3 : optAbcLength
    return (new Date().getTime()).toString(36) + ns.arbitraryString(abcLength)
  }

  /**
   * randBetween
   * get an random number between x and y
   * @param {number} min the lower bound
   * @param {number} max the upper bound
   * @return {number} the random number
   **/
  ns.randBetween = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min

  /*
 * settimeout as a promise
 * @param {number} ms number of ms to wait
 * @param {*} [tag] optional tag that will be returned when resolved
 */
  ns.loiter = (ms, tag) => {
    return new Promise(function (resolve, reject) {
      try {
        setTimeout(function () {
          resolve(tag);
        }, ms);
      } catch (err) {
        reject(err);
      }
    });
  };
  /**
   * whether something has expired from its run id
   */
  ns.progressExpired = ({ runID, timeout }) => {
    const d = ns.getDateFromRunID({ runID }).getTime();
    const expired = new Date().getTime() - timeout
    return d < expired
  };


  /**
   * get an arbitrary alpha string
   * @param {number} length of the string to generate
   * @return {string} an alpha string
   **/
  ns.arbitraryString = (length) => {
    var s = ''
    for (var i = 0; i < length; i++) {
      s += String.fromCharCode(ns.randBetween(97, 122))
    }
    return s
  }




  // mock a filmmaster from a vimeo response
  // TODO get aspect ratios, definition formats and framerates
  ns.mockFilmMasterFromVimeo = ({ vimeoFile, vimeoUser, aspectRatios, frameRates, definitionFormats }) => {
    // first make the filmmaster - a lot of this stuff is null
    if (!vimeoFile) return null
    const runID = ns.generateUniqueString()
    // this is possible, but unlikely
    vimeoUser = vimeoUser || {}
    const filmMaster = {
      id: null,
      filmID: null,

      // not available
      frames: null,
      // these are calculated later
      definitionFormatID: null,
      frameRateID: null,

      // this is not avail
      interlaceTypeID: null,
      bitsPerPixel: null,

      hostUrl: vimeoFile.link,
      hostID: vimeoFile.id,
      hostType: 'vimeo',
      duration: vimeoFile.duration,
      runID,
      hostUserName: vimeoUser.name,
      hostUserProfile: vimeoUser.link,
      // this is actually the uri - maybe it should be the id - but thats what we have now
      hostUserID: vimeoUser.uri,

      // not available TODO - probably needs deleted this field as it should be dynamic
      personID: null,

      // if there are no video files, we'll use this
      embed: vimeoFile.embed && vimeoFile.embed.html
    }

    // now make the filmfiles if there are any
    // first combine the downloads and film files
    const files = (vimeoFile.files || [])
    const downloads = (vimeoFile.download || [])
    const combined = files.concat(downloads)
      .reduce((p, c) => {
        const mKey = c.md5 + '_' + c.quality
        p[mKey] = c
        return p
      }, {})

    const filmFiles = Object.keys(combined).length ? Object.keys(combined).map(k => {
      const f = combined[k]
      const nearestar = getNearestAspectRatioID(aspectRatios || [], f.width, f.height)
      // the source file provides this overall info
      if (f.type.toLowerCase() === 'source') {
        const nearestfr = getNearestFrameRateID(frameRates || [], f.fps)
        filmMaster.frameRateID = nearestfr && nearestfr.id
        const definitionFormat = getMatchingDefinitionFormat(definitionFormats, f.quality)
        filmMaster.definitionFormatID = definitionFormat && definitionFormat.id
      }
      return {
        id: null,
        path: f.link,
        filmMasterID: null,
        runID,
        md5: f.md5,
        hasDRM: false,
        width: vimeoFile.width,
        height: vimeoFile.height,
        aspectRatioID: (nearestar && nearestar.id) || null,
        distributionFormat: f.quality,
        // the source file will have an invalid mimetype of "source"
        mimeType: f.type,
        targetName: f.width && f.height ? f.width + 'x' + f.height : f.quality,
        size: f.size,
        source: f.type.toLowerCase() === 'source',
        fps: f.fps
      }
    }) : null
    filmMaster.FilmFiles = filmFiles
    return filmMaster
  }

  // just a digester
  ns.hasher = (...args) => {
    return hasher(args)
  }

  // remove a given set of keys from an ob
  ns.removeProperties = ({ ob, skipKeys, recursive }) => {
    const keys = is.array(skipKeys) ? skipKeys : [skipKeys]

    // make a shallow copy
    const recurse = (sob) => {
      // we're done
      if (is.nullOrUndefined(sob) || !is.object(sob)) return ob
      // recurse through the props
      return Object.keys(sob).reduce((p, c) => {
        // if its not a skip key then keep it
        if (keys.indexOf(c) === -1) {
          // if its an object, we need to get more
          const v = is.object(sob[c]) && recursive ? recurse(sob[c]) : sob[c]
          // slightly different handling of arrays and objects
          if (is.array(sob)) {
            p.push(v)
          } else {
            p[c] = v
          }
        }
        return p
      }, is.array(sob) ? [] : {})
    }
    // kick the thing off
    return recurse(ob)
  }

  // filter out matching matchreasons
  ns.filterReasons = (item, itemType) => {
    if (!item) return []
    // the point of this is make an array of matchreasons copying the item information, but filtering out only a given type of match
    const filteredReasons = [{ ...item, MatchReasons: (item.MatchReasons || []).filter(f => f.matchType === itemType) }]
    return filteredReasons
  }

  // some sorting utilities
  const isNumber = (x) => is.number(parseFloat(x))
  const isUndefined = (x) => is.nullOrUndefined(x)
  const valueCompare = (v1, v2, pluck) => {
    const x = pluck ? pluck(v1) : v1
    const y = pluck ? pluck(v2) : v2
    return isNumber(x) && isNumber(y) ? +x - y : (x > y || isUndefined(y) ? 1 : (x < y || isUndefined(x) ? -1 : 0))
  }
  ns.aaSort = (arr, pluckValue, pluckArray) => arr.sort((aa, bb) => {
    const a = pluckArray ? pluckArray(aa) : aa
    const b = pluckArray ? pluckArray(bb) : bb
    return a.reduce((p, c, i) => p || valueCompare(c, b[i], pluckValue), 0) || (b.length > a.length && -1)
  })
  ns.pluralize = (text) => {
    const plurals = {
      Person: 'People',
      Child: 'Children',
      y: 'ies',
      ss: 'sses',
      x: 'xes',
      h: 'hes',
      sis: 'ses'
    }
    const pk = Object.keys(plurals).find(f => text.match(new RegExp(`${f}$`)))
    return pk ? text.replace(new RegExp(`${pk}$`), plurals[pk]) : `${text}s`
  }
  ns.unCamel = ({ text, pluralize, capAll }) => {
    // change camel case to a sentence
    const result = (text || '')
      .split('')
      .reduce((p, c) => {
        // if its the 1st character uppercase it
        if (!p) {
          p += c.toUpperCase()
        } else if (c.toUpperCase() === c) {
          // so its uppercase, we need a space
          p += ' '
          // and maybe we're capitalizing all breaks
          p += (capAll ? c : c.toLowerCase())
        } else {
          p += c
        }
        return p
      }, '')
    return result && pluralize ? ns.pluralize(result) : result
  }
  // flatten out a tree, keep as an array
  ns.treeUntangleArray = ({ item, arr, localizedKey }) => {
    arr = arr || []
    if (item) {
      arr.push({
        text: (localizedKey ? item[localizedKey] && item[localizedKey].name : item.name) || '',
        itemOrder: item.itemOrder
      })
      ns.treeUntangleArray({ item: item.Parent, arr, localizedKey })
    }
    return arr
  }

  // flatten out a tree structure
  ns.treeUntangle = ({ item, localizedKey, keepOrder, separator }) => {
    const text = ns.treeUntangleArray({ item, localizedKey }).map(f => f.text)
    return (keepOrder ? text : text.reverse()).filter(f => f).filter((f, i, a) => a.indexOf(f) === i).join(separator || '/')
  }

  // get profession text
  ns.getRelationshipTypeName = ({ item }) => {
    return ns.getLocalized({ item, key: 'RelationshipType' })
  }
  ns.getProfessionName = ({ item }) => {
    return ns.getLocalized({ item, key: 'Profession' })
  }
  ns.getLocalized = ({ item, key }) => {
    const base = item && item[key]
    return (base && base[key + 'Localized'] && (base[key + 'Localized'].name)) || ''
  }

  // remove multiple spaces etc
  ns.clean = (text) => (text || '').replace(/\s+/g, ' ').trim()

  // remove dup from match text
  ns.undup = (text) => ns.clean((text || '').split(' ').filter((d, i, a) => d && a.indexOf(d) === i).join(' '))

  // construct a name from an item
  ns.getPersonName = ({ item }) => {
    const ip = item.Person
    return ns.undup(ip ? [ip.firstName, ip.middleName, ip.lastName, ip.professionalName, ip.pseudonym].filter(e => e).join(' ') : '')
  }
  ns.getBrandStory = ({ item }) => {
    const ip = item.Brand
    return ns.clean((ip && ip.strategy) || '')
  }
  ns.getProductStory = ({ item }) => {
    return ns.getProductCategory({ item })
  }
  ns.getAwardStory = ({ item, allPrizes }) => {
    // if allPrizes is specified the idea is to show
    // all known prizes for this award, rather than the detail in the reason
    // because we are not providing a story on an individual award, but rather

    // all the prizes available
    const knownPrizes = item && allPrizes && allPrizes.find(f => item.awardID === f.id)
    if (knownPrizes) {
      const prizes = knownPrizes && knownPrizes.AwardToAwardPrizes
      return (prizes || []).map(f => ({
        type: 'text',
        text: (f.AwardPrizeLocalized && f.AwardPrizeLocalized.name) || (knownPrizes.AwardLocalized && knownPrizes.AwardLocalized.name) || ''
      }))
    }

    // if we're not doing all the prizes available, and there is no more detail on the award available, just return its name

    let v = item && item.AwardPrizeMatchItems
    if (!v || !v.length) {
      return [{
        type: 'text',
        text: ns.getAwardName({ item: item })
      }]
    }

    // hoping to find some more detail about the award here - so it will apply to an individual person or film.
    // this sorting causes an endless vue loop - so dont so that here - leaving it as a warning
    // v = ns.aaSort(v, null, item => [item.theYear || 0, (item.DeepAward && item.DeepAward.itemOrder) || 0])
    // loads of stuff so blow out the the ancestors/year and  prize info
    return v.map(f => {
      const { theYear, DeepAward } = f
      const v = DeepAward && DeepAward.ViewAwardAncestor && DeepAward.ViewAwardAncestor.AwardAncestors
      const n = ns.getAwardPrizeName({ item: f })
      return {
        type: 'text',
        text: `${theYear ? theYear + ' ' : ''}${(v || []).map(d => d.name).join(' - ')}${n ? ' (' + n + ')' : ''}`
      }
    })
  }

  ns.getCompanyStory = ({ item }) => {
    const ip = item && item.Company
    return ns.clean((ip && ip.detail) || '')
  }
  ns.getVimeoUserStory = ({ item }) => {
    const ip = item.VimeoUser
    return ns.clean((ip && (ip.bio)) || '')
  }

  ns.getVimeoFilmStory = ({ item }) => {
    const ip = item.VimeoUserVideo
    return ns.clean((ip && (ip.description)) || '')
  }

  ns.getPersonStory = ({ item }) => {
    const ip = item.Person
    return ns.clean((ip && (ip.biography || ip.philosophy)) || '')
  }
  ns.getPersonBiography = ({ item }) => {
    const ip = item.Person
    return ns.clean((ip && ip.biography) || '')
  }
  // construct a name from an item
  ns.getBrandName = ({ item }) => {
    return ns.clean((item.Brand && item.Brand.name) || '')
  }

  ns.getProductName = ({ item }) => {
    return ns.clean((item.Product && item.Product.name) || '')
  }

  ns.getCompanyName = ({ item }) => {
    const ip = item.Company && item.Company.CompanyNames
    return ns.clean((ip && ip.map(f => f.name).join('/')) || '')
  }

  ns.getAwardPrizeName = ({ item }) => {
    return ns.getLocalized({ item, key: 'AwardPrize' })
  }

  ns.getAwardName = ({ item }) => {
    return ns.getLocalized({ item, key: 'Award' })
  }

  ns.getCompanyType = ({ item }) => {
    return ns.clean(ns.treeUntangle({ item: item.CompanyType, localizedKey: 'CompanyTypeLocalized' }))
  }

  ns.getProductCategory = ({ item }) => {
    return ns.clean(ns.treeUntangle({ item: item.ProductCategory, localizedKey: 'ProductCategoryLocalized' }))
  }

  ns.getBrandUrl = ({ item }) => {
    return ns.clean((item.Brand && item.Brand.url) || '')
  }

  ns.getBrandStrategy = ({ item }) => {
    return ns.clean((item.Brand && item.Brand.strategy) || '')
  }

  ns.getFilm = ({ item }) => item && item.Film
  ns.getFilmMasters = ({ item }) => {
    const film = ns.getFilm({ item })
    return film && film.FilmMasters
  }
  ns.getFilmMaster = ({ item, masterIndex }) => {
    const filmMasters = ns.getFilmMasters({ item })
    return filmMasters && filmMasters[masterIndex || 0]
  }
  // get the all the poster frames
  ns.getPosterFrames = ({ item, masterIndex = 0, filmMaster = null }) => {
    return ns.getPosterFramesFromMaster({ filmMaster: filmMaster || ns.getFilmMaster({ item, masterIndex }) })
  }

  ns.getPosterFramesFromMaster = ({ filmMaster }) => {

    const pf = {
      filmMaster,
      posterFrames: filmMaster && filmMaster.FilmMasterPosterFrames,
      masterAspectRatio: ns.getMasterAspectRatio({ filmMaster })
    }

    return pf

  }

  // get the default poster frame
  ns.getDefaultPosterFrame = ({ item, filmMaster }) => {
    const { filmMaster: fm, posterFrames, masterAspectRatio } = ns.getPosterFrames({ item, filmMaster })
    // now find the default one, us the first if none given
    const defaultPosterFrame = posterFrames ? (posterFrames.find(f => f.defaultPosterFrame) || posterFrames[0]) : null
    return {
      filmMaster: fm,
      defaultPosterFrame,
      masterAspectRatio
    }
  }

  // used to see if an aspect ratio is good enough to use
  ns.closeEnough = ({ target, value, tolerance }) => {
    // within tolerance %
    // TODO - this could be improved with ranking based on requied quality and closeness
    // but since there is usually only a couple of qualifiers pre film for now, review when there are more
    // default is a very close match
    tolerance = tolerance || 0.2
    const d = Math.abs(target - value)
    /// console.log(d, target, value, tolerance)
    return d <= target * tolerance
  }

  // look at files and see which ones are close enough to the target aspect ratio
  ns.closeAspectMatches = ({ files, target, tolerance }) => {
    return files && files.filter(g => (Object.prototype.hasOwnProperty.call(g, 'path') ? g.path : g.link) && ns.closeEnough({
      target,
      value: Object.prototype.hasOwnProperty.call(g, 'widthPx') ? g.widthPx / g.heightPx : g.width / g.height,
      tolerance
    }))
  }

  // return an actionable obect with best and worst file picked out
  ns.prioritizeAspectFiles = ({ files, targetAspect, tolerance, minHeight }) => {
    // 5% default
    tolerance = tolerance || 0.2
    const model = {
      best: null,
      worst: null,
      poster: null,
      files,
      targetAspect,
      tolerance,
      candidates: (files && files.slice()) || [],
      matched: false
    }

    const candidates = ns.closeAspectMatches({
      target: targetAspect,
      files,
      tolerance
    })

    // found some, if none found then just all the files are available to use
    model.matched = candidates && candidates.length
    if (!model.matched) {
      console.log('couldnt find good enough aspect ratios - using all files', tolerance, targetAspect)
    } else {
      model.candidates = candidates
    }

    // sorty by quality
    // there are differnt properties from differnt sources
    model.candidates = model.candidates.sort((a, b) => Object.prototype.hasOwnProperty.call(a, 'widthPx') ? (a.widthPx - b.widthPx) : (a.width - b.width))
    model.worst = model.candidates[0] || null
    // the poster is normally the first one that is of sufficient quality (judged by height)
    // graphql queries should probably have filtered that out already, but vimeo ones may not have
    model.poster = (minHeight ? model.candidates.find(f => {
      const height = Object.prototype.hasOwnProperty.call(f, 'heightPx') ? f.heightPx : f.height
      return height >= minHeight
    }) : null) || model.worst
    model.best = model.candidates.length ? model.candidates[model.candidates.length - 1] : null
    return model
  }



  /**
   * TODO !important - this is all wrong and needs to be worked out according to the player size - which we don't have here so it'll need to come from the player on change of frame size
   * get the defaultposter frame for a given size
   * @param {object} item a single item containing a film record
   * @param {boolean} [big=false] a big one to choose quality ?
   * @param {number} [aspectRatio] the preferred aspect ratio width/height
   * @param {number} [minHeight] the preferred minHeight width/height (graphql queries will already have filteredout those too small)
   */
  ns.getPosterFrameFiles = ({ item, big, minHeight, filmMaster }) => {
    // get the default one
    const { masterAspectRatio, defaultPosterFrame } = ns.getDefaultPosterFrame({ item, filmMaster })
    // the artwork is a separate one
    const artwork = defaultPosterFrame && defaultPosterFrame.Artwork
    const files = artwork && artwork.ArtworkToFiles && artwork.ArtworkToFiles.length && artwork.ArtworkToFiles
    if (!files) return null
    // find the best aspect ratio for the poster frame set
    const matches = ns.prioritizeAspectFiles({
      files,
      targetAspect: masterAspectRatio,
      minHeight
    })
    if (!matches.matched) {
      console.log('couldnt find good enough aspect ratios at tolerance', matches.tolerance)
    }

    // best and worst quality options
    return big ? matches.best : matches.poster
  }

  ns.getMasterAspectRatio = ({ filmMaster }) => {
    // we dont actually have the aspect ratio except in the individual film files .. so trawl them and take the average
    if (!filmMaster || !filmMaster.FilmFiles) return null
    const files = filmMaster.FilmFiles.filter(f => f.width && f.height)
    if (!files.length) return null
    return files.reduce((p, c) => p + (c.width / c.height), 0) / files.length
  }

  // return the path of a default poster frame artwork file for a given size
  ns.getPosterFrame = ({ item, big, minHeight, filmMaster }) => {
    // TODO - a placeholder for when there is no poster frame ?
    const file = ns.getPosterFrameFiles({ item, big, minHeight, filmMaster })
    return file ? file.path : null
  }

  ns.getVimeoVideoPosterFrame = ({ item, big, minHeight }) => {
    const video = item && item.VimeoUserVideo
    if (!video) return null
    // now find the aspect ratio
    const targetAspect = video.height && video.width && (video.width / video.height)
    // the default picture is already in the video object
    const { pictures } = video
    const defaultPicture = pictures && pictures.active
    if (!defaultPicture) {
      console.log('no default picture found')
      return null
    }
    // now to get the best file
    const matches = ns.prioritizeAspectFiles({
      files: pictures.sizes,
      targetAspect,
      minHeight
    })
    const result = big ? matches.best && matches.best.link : matches.poster && matches.poster.link
    return result
  }

  // extract film name from an item
  ns.getFilmName = ({ item }) => {
    return ns.clean((item.Film && item.Film.filmName) || '')
  }

  ns.getVimeoFilmName = ({ item }) => {
    const v = item && item.VimeoUserVideo
    const n = (v && v.name) || ''
    const d = v && v.duration
    return n + (d ? ` (${d}s)` : '')
  }

  ns.getFilmSynopsis = ({ item }) => {
    return ns.clean((item.Film && item.Film.synopsis) || '')
  }
  /**
  * since hosting this on storage, this has become a little more complex
  * a storage path = 'https://storage.googleapis.com/fid-home-hosting/images/vimeo/285741584/726638609-100x75.jpeg'
  * equivalent file on vimeo = 'https://i.vimeocdn.com/video/726638609-86de19e1eadbac4ad91a3718f9cc40dbd25c742962ccb7b0a809e06635d13853-d_100x75?r=pad
  * so compare vimeo uri is 
  * const x = .*\/(\d+).*[-_](\d+x\d+).*
  * matchVimeoUri (storageUri, vimeoUri) => vimeoUri.replace(x, '$1_$2') === storageUri.replace(x,'$1_$2')
  */
  ns.matchVimeoUri = (vimeoUri, storageUri) => {
    const x = /.*\/(\d+).*[-_](\d+x\d+).*/
    return vimeoUri.replace(x, '$1_$2') === storageUri.replace(x, '$1_$2')
  }

  // this can be handy to traverse an object to find a given key without caring too much where it is or worrying about nulls in the way
  ns.pluckObjectByKey = (key, ob) => {
    if ((typeof ob !== 'object' && !Array.isArray(ob)) || ob === null) return null
    return Object.keys(ob)
      .reduce((found, k) => {
        return found || (k === key ? ob[k] : ns.pluckObjectByKey(key, ob[k]))
      }, null)
  }

  // what comes back from the search is something like
  // [{ itemType, itemID, itemMatch, matchReasons:[] }]
  // lets say itemtype is film, then matchreasons will contain 1 or more item with filmID = item ID
  // this provides the base info for many of the views, so we'll make a pointer to it now to avoid repeated searches later
  // also accumulate anything that'll be part of a list
  ns.optimizeFetched = ({ fetched }) => {
    fetched.forEach(d => {
      // we're looking for something like filmID
      const idField = d.itemType + 'ID'
      d.itemReason = d.MatchReasons.find(f => f[idField] === d.itemID)
      if (!d.itemReason) {
        console.log(`itemtable: failed to find itemreason for ${idField}`, d)
      }
    })
    return fetched
  }

  ns.getArtworkLink = ({ base }) => (base && base.Artwork &&
    base.Artwork.ArtworkToFiles.length &&
    base.Artwork.ArtworkToFiles[0].ArtworkArtifact &&
    base.Artwork.ArtworkToFiles[0].ArtworkArtifact.SignedUrl &&
    base.Artwork.ArtworkToFiles[0].ArtworkArtifact.SignedUrl.url) || ''

  // if we're downloading then we want the original
  // otherwise we want the pdf
  ns.getArtworkRef = ({ base, download }) => {
    const prop = download ? 'SignedUrlDownload' : 'SignedUrl'
    const farts = base &&
      base.Artwork &&
      base.Artwork.ArtworkToFiles;
    const tart = farts && farts.find(f => {
      const t = (download && f.original) ||
        (!download && f.imageType === 'application/pdf')
      if (!f.imageType) console.log('....there was no imageType', f)

      return t
    })


    return (tart &&
      tart.ArtworkArtifact &&
      tart.ArtworkArtifact[prop] &&
      tart.ArtworkArtifact[prop].url) || ''
  };

  // this is used to get an image with an artifact
  ns.getArtifactImage = ({ item, key }) => {
    // TODO deal with signURL expiry
    // deal with loading and default image
    const base = item && item[key] && (Array.isArray(item[key]) ? item[key][0] : item[key])
    return ns.getArtworkLink({ base })
  }

  // gets the correct type of text given a match type
  ns.getTextFromReason = ({ reason, rowType }) => {
    // the targetType will take precedence when provided
    if (!reason) return ''
    switch (rowType || reason.matchType) {
      case 'film':
        return ns.getFilmName({ item: reason })

      case 'person':
        return ns.getPersonName({ item: reason })

      case 'brand':
        return ns.getBrandName({ item: reason })

      case 'product':
        return ns.getProductName({ item: reason })

      case 'company':
        return ns.getCompanyName({ item: reason })

      case 'award':
        return ns.getAwardName({ item: reason })

      case 'vimeouser':
        return (reason && reason.VimeoUser && reason.VimeoUser.name) || ''

      case 'vimeofilm':
        return ns.getVimeoFilmName({ item: reason })

      case 'user':
        return (reason && reason.ProviderAuth && reason.ProviderAuth.displayName) || ''

      default:
        return ns.undup(reason.match)
    }
  }

  // gets the correct type of text given a match type
  // the "story" differs for each reason type, and will be an array
  // of info to be displayed where there's room to do it
  ns.getStoryFromReason = ({ reason, rowType, allPrizes }) => {
    if (!reason) return null
    const reasonType = rowType || reason.matchType
    switch (reasonType) {
      case 'film':
        return [{
          type: 'text',
          text: ns.getFilmSynopsis({ item: reason })
        }]
      case 'person':
        return [{
          type: 'text',
          text: ns.getPersonStory({ item: reason })
        }]

      case 'brand':
        return [{
          type: 'text',
          text: ns.getBrandStory({ item: reason })
        }]

      case 'product':
        return [{
          type: 'text',
          text: ns.getProductStory({ item: reason })
        }]

      case 'company':
        return [{
          type: 'text',
          text: ns.getCompanyStory({ item: reason })
        }]

      case 'award':
        // it's already an array
        return ns.getAwardStory({ item: reason, allPrizes })

      case 'vimeouser':
        return [{
          type: 'text',
          text: ns.getVimeoUserStory({ item: reason })
        }]

      case 'vimeofilm':
        return [{
          type: 'text',
          text: ns.getVimeoFilmStory({ item: reason })
        }]

      default:
        console.log('unknown type requesting story', reasonType)
    }
  }

  ns.getUrlFromReason = ({ reason, rowType }) => {
    if (!reason) return ''
    switch (rowType || reason.matchType) {
      case 'film':
      case 'person':
        return ''

      case 'brand':
        return ns.clean((reason.Brand && reason.Brand.url) || '')

      case 'product':
        return ns.clean((reason.Product && reason.Product.url) || '')

      case 'company':
        return ns.clean((reason.Company && reason.Company.url) || '')

      default:
        return ''
    }
  }
  // get the prize image
  ns.getImageFromAward = ({ award }) => {
    if (!award) return null
    return ns.getArtifactImage({ item: award && award.AwardPrize, key: 'AwardPrizeToArtworks' })
  }

  // special for getting a matching vimeo picture
  const getVimeoPicture = ({ pictures, big }) => {
    if (pictures && pictures.sizes) {
      const pic = pictures.sizes.reduce((p, c) => {
        if (!p) return c
        if (big) {
          return c.height > p.height ? c : p
        } else {
          return c.height < p.height ? c : p
        }
      }, null)
      return pic && pic.link
    }
    return null
  }

  // gets the correct type of image given a match type
  // if the row type is given, then this is the 'master' reason for the row
  // otherwise the text chosen is driven by the reason itself
  ns.getImageFromReason = ({ reason, rowType, big, minHeight, filmMaster }) => {
    if (!reason) return ''
    switch (rowType || reason.matchType) {
      case 'film':
        return ns.getPosterFrame({ item: reason, big, minHeight, filmMaster })

      case 'person':
        return ns.getArtifactImage({ item: reason.Person, key: 'PersonToArtworks' })

      case 'brand':
        return ns.getArtifactImage({ item: reason, key: 'Brand' })

      case 'product':
        return ns.getArtifactImage({ item: reason, key: 'Product' })

      case 'company':
        return ns.getArtifactImage({ item: reason && reason.Company, key: 'CompanyToArtworks' })

      case 'award':
        return ns.getArtifactImage({ item: reason && reason.AwardPrize, key: 'AwardPrizeToArtworks' })

      case 'vimeofilm':
        return ns.getVimeoVideoPosterFrame({ item: reason, big, minHeight })

      case 'vimeouser':
        return getVimeoPicture({ pictures: reason && reason.VimeoUser && reason.VimeoUser.pictures, big })

      case 'user':
        return reason && reason.ProviderAuth && reason.ProviderAuth.photoUrl

      default:
        return null
    }
  }

  /**
   * gets rid of extra items like dups from a list and shortens it if necessary
   */
  const trimList = ({ maxListShow, list }) => {
    const cleaner = list.filter(r => r).filter((r, i, a) => a.indexOf(r) === i)
    if (maxListShow && cleaner.length > maxListShow) {
      const shorter = cleaner.slice(0, maxListShow)
      shorter.push('...')
      return shorter
    }
    return cleaner
  }

  /**
   *this is to do with call sheet resolution
   *the idea is to make a matchreason style item from
   *a fully made up item, so it can be used to generated a person/profession chip makeTy
   * @param {object} item the original searcher result
   * @param {string} makeType what we are making (person for now)
   * @param {object[]} makeBase what we are making it from
   * @return {object} a matchreason as would be returned from a searcher
   */
  ns.makeMatchReasonsFromItem = ({ item, makeType, makeBase }) => {

    if (makeType === 'person') {
      const makeReasons = item.MatchReasons.map(f => ({
        ...f,
        matchType: makeType,
        ProfessionMatchItems: makeBase.map(g => ({
          Profession: g.Profession,
          Company: g.Company,
          cameo: g.cameo,
          hidden: g.hidden
        }))
      }))
      return makeReasons
    } else {
      console.log('dont know how to makereasonfromitem for type', makeType)
      return null;
    }
  }
  /**
   * get awards out
   */
  ns.getAwardsFromReason = ({ reason, rowType }) => {
    rowType = rowType || reason.matchType
    if (rowType === 'film') {
      const f = reason && reason.Film && reason.Film.FilmToAwards
      return f && f.length ? f : null
    }
    if (rowType === 'person') {
      const p = reason && reason.Person && reason.Person.PersonToAwards
      return p && p.length ? p : null
    }
    return null
  }
  /**
   * typically only the first item is of interest
   * however a professions and company types can be multiple so we need to join them
   */
  ns.getBigTextFromReason = ({ maxListShow, reason, rowType }) => {
    // since these are called from computed properties, its possible theyll have no value yet
    if (!reason) return ''
    const { AwardPrizeMatchItems, ProfessionMatchItems, CompanyTypeMatchItems, RelationshipTypeMatchItems } = reason
    // the basic text
    let text = ns.getTextFromReason({ reason, rowType })

    // add the award name if we have it
    if (AwardPrizeMatchItems && AwardPrizeMatchItems.length) {
      const prizeNames = trimList({ list: AwardPrizeMatchItems.map(r => ns.getAwardPrizeName({ item: r })), maxListShow })
      return text + (prizeNames.length ? ' (' + prizeNames.join(', ') + ')' : '')
    }
    // we also can have various relationship types
    if (RelationshipTypeMatchItems && RelationshipTypeMatchItems.length) {
      const relationshipTypeNames = trimList({ list: RelationshipTypeMatchItems.map(r => ns.getRelationshipTypeName({ item: r })), maxListShow })
      return text + (relationshipTypeNames.length ? ' (' + relationshipTypeNames.join(', ') + ')' : '')
    }
    // now add any extra stufffmatch

    if (ProfessionMatchItems && ProfessionMatchItems.length) {
      // need to dedup, because different IDS can return the same text
      // so since we're only showing text, may as well dump the dups
      const professionNames = trimList({ list: ProfessionMatchItems.map(r => ns.getProfessionName({ item: r })), maxListShow })
      return text + (professionNames.length ? ' (' + professionNames.join(', ') + ')' : '')
    }
    if (CompanyTypeMatchItems && CompanyTypeMatchItems.length) {
      const companyTypes = trimList({ list: CompanyTypeMatchItems.map(r => ns.getCompanyType({ item: r })), maxListShow })
      return text + (companyTypes.length ? ' (' + companyTypes.join(', ') + ')' : '')
    }
    return text
  }

  const accumulateRelationshipTypes = ({ ob, relationshipType }) => {
    // now accumulate the professions
    if (!relationshipType) return ob

    ob.RelationshipTypeMatchItems = ob.RelationshipTypeMatchItems || []
    ob.RelationshipTypeMatchItems.push({
      relationshipTypeID: relationshipType.id,
      RelationshipType: relationshipType
    })

    // just keep these sorted
    if (ob.RelationshipTypeMatchItems.length > 1) {
      ob.RelationshipTypeMatchItems = ns.aaSort(ob.RelationshipTypeMatchItems, item => item.itemOrder, arr => [arr.RelationshipType])
    }
    return ob
  }
  // support functions for making searchers
  const accumulateProfessions = ({ ob, linkEntry }) => {
    // now accumulate the professions
    const { Profession: profession, Company, cameo } = linkEntry || {}
    if (!profession) return ob
    ob.ProfessionMatchItems = ob.ProfessionMatchItems || []
    const pob = {
      professionID: profession.id,
      Profession: profession,
    };
    // some items have some additional useful info in the linkEntry
    pob.Company = Company
    pob.cameo = cameo
    ob.ProfessionMatchItems.push(pob)
    // just keep these sorted
    if (ob.ProfessionMatchItems.length > 1) {
      ob.ProfessionMatchItems = ns.aaSort(ob.ProfessionMatchItems, item => item.itemOrder, arr => [arr.Profession])
    }
    return ob
  }
  const accumulatePrizes = ({ ob, prize, deep, theYear }) => {
    // now accumulate the professions
    if (!prize && !deep && !theYear) return null
    ob.AwardPrizeMatchItems = ob.AwardPrizeMatchItems || []
    ob.AwardPrizeMatchItems.push({
      awardPrizeID: prize && prize.id,
      AwardPrize: prize,
      DeepAward: deep,
      theYear
    })
    // just keep these sorted
    if (ob.AwardPrizeMatchItems.length > 1) {
      ob.AwardPrizeMatchItems = ns.aaSort(ob.AwardPrizeMatchItems, item => item ? [item.theYear, item.itemOrder] : 0, arr => [arr.AwardPrize])
    }
    return ob
  }
  const accumulateCompanyTypes = ({ ob, companyType }) => {
    // now accumulate the professions
    if (!companyType) return ob
    ob.CompanyTypeMatchItems = ob.CompanyTypeMatchItems || []
    ob.CompanyTypeMatchItems.push({
      companyTypeID: companyType.id,
      CompanyType: companyType
    })
    // just keep these sorted
    if (ob.CompanyTypeMatchItems.length > 1) {
      ob.CompanyTypeMatchItems = ns.aaSort(ob.CompanyTypeMatchItems, item => item.itemOrder, arr => [arr.CompanyType])
    }
    return ob
  }

  /**
   * the idea here is to the link table from gtaphql query
   * to make the responses look like the reasons that arrived from a search query
   * that way we can at least start to show detail pages initially from the limited search query data
   * whilst the graphql is going on, and replace that when the full data arrives and gets restructired below
   * it's possible that this could become a server side task at some point but it would limit flexibility
   * for new client stuff so for now, this is the best place for it
   * @param {object[]} linkTable  something like BrandToFilms
   * @param {string} target the name of the target object eg 'Film'
   * @param {object} subject the object being organized eg Brand
   * @param {string} matchType the matchtype to assign eg 'film'
   * @param {string} matchField where to get the match text from eg 'filmName'
   * @param {function} accumulate sometimes a further accumulation (eg professions is required)
   * @return {object[]} the generated array of matches
   */
  ns.generateObMatches = ({ linkTable, target, subject, matchType, matchField, accumulate }) => {
    // we're relying on the apollo typenames to organize this
    const typeName = ns.reportIfMissing(subject.__typename, `subject typename ${target}`)
    return (linkTable || []).reduce((p, c) => {
      const targetOb = c[target]
      // const targetType = ns.reportIfMissing(targetOb.__typename, `target typename ${target}`)
      const targetObId = ns.reportIfMissing(targetOb.id, `target id ${target}`)
      let ob = p.find(f => ns.reportIfMissing(f[target].id, `p id ${target}`) === targetObId)
      // first time we're seeing  one
      if (!ob) {
        ob = {
          match: targetOb[matchField],
          matchType,
          matchID: targetObId
        }
        ob[matchType + 'ID'] = ns.reportIfMissing(subject.id, `subject.id ${target}`)
        ob[target + 'ID'] = ns.reportIfMissing(targetOb.id, `targetOb.id ${target}`)
        ob[typeName] = subject
        ob[target] = targetOb
        p.push(ob)
      }
      if (accumulate) {
        accumulate({ ob, linkEntry: c })
      }
      return p
    }, [])
  }
  // curry the sort and combine all that
  // these kind of sorts know how to extract out an array of keys and sort the arrray based on the order of the keys
  // in others words, pluck out the item orders to respect the proper ordering of the professions and company types
  const sortByMatch = (items) => ns.aaSort(items, null, arr => [arr.match])
  const sortByProfession = (items) => ns.aaSort(items, item => (item && item.Profession && item.Profession.itemOrder) || 0, arr => arr.ProfessionMatchItems || [])
  const sortByAward = (items) => ns.aaSort(items, item => (item && item.Award && item.Award.itemOrder) || 0, arr => arr.AwardMatchItems || [])
  const sortByCompanyType = (items) => ns.aaSort(items, item => (item && item.CompanyType && item.CompanyType.itemOrder) || 0, arr => arr.CompanyTypeMatchItems || [])
  ns.reportIfMissing = (item, text) => {
    if (is.nullOrUndefined(item)) {
      console.log(`item ${text} was unexpectedly missing `, typeof item)
    }
    return item
  }

  // in the award page the accumulation is partly done already by the gql quiery
  // this moves it to the place the others accumulate to
  const standardizeAwardPageItems = ({ ob, linkEntry }) => {
    const { AwardPrizeMatchItems } = linkEntry
    // move over matchitems from awardpage query
    if (AwardPrizeMatchItems) {
      // add a deepaward object
      AwardPrizeMatchItems.forEach(f => {
        // this is not here in the awardpagequery
        // to avoid repeating an expensive AwardAncestors query
        // that had anyway to be done as part of the search
        // so we just need to move it to the right place
        // to make it the same as the other main pages
        // TODO - find itemorder from somewhere in the object
        f.DeepAward = {
          id: f.deepAwardID,
          ViewAwardAncestor: {
            id: f.deepAwardID,
            itemOrder: 0,
            AwardAncestors: f.AwardAncestors
          }
        }
      })
      ob.AwardPrizeMatchItems = AwardPrizeMatchItems
    }
    return ob
  }

  ns.generateFilmReason = ({ linkTable, subject }) => {
    // the film reasons, reduce to 1 per film

    const after = ns.generateObMatches({
      linkTable,
      target: 'Film',
      subject,
      matchType: 'film',
      matchField: 'filmName',
      accumulate: ({ ob, linkEntry }) => {
        standardizeAwardPageItems({ ob, linkEntry })
        accumulateProfessions({ ob, linkEntry })
      }
    })
    return after
  }

  // a patch to the linkTable is requried for awards
  // because the returned awardID is the lowest level, but we need the highest
  // dont really like to do this but needs must

  const patchAwardLinkTable = ({ linkTable }) => {
    if (linkTable) {
      linkTable.forEach(f => {
        const v = f.DeepAward && f.DeepAward.ViewAwardAncestor && f.DeepAward.ViewAwardAncestor && f.DeepAward.ViewAwardAncestor.AwardAncestors
        if (v && v.length) {
          f.Award = {
            id: v[0].id,
            AwardLocalized: {
              id: v[0].id,
              name: v[0].name
            }
          }
        }
      })
    }
    return linkTable
  }

  ns.generateAwardReason = ({ linkTable, subject }) => {
    return ns.generateObMatches({
      linkTable: patchAwardLinkTable({ linkTable }),
      target: 'Award',
      subject,
      matchType: 'award',
      matchField: 'id',
      accumulate: ({ ob, linkEntry }) => {
        accumulatePrizes({ ob, prize: linkEntry.AwardPrize, deep: linkEntry.DeepAward, theYear: linkEntry.theYear })
      }
    })
  }
  ns.generateProductReason = ({ linkTable, subject, key }) => {
    // the film reasons, reduce to 1 per film
    return ns.generateObMatches({
      linkTable,
      target: 'Product',
      subject,
      matchType: 'product',
      matchField: 'name',
      accumulate: ({ ob, linkEntry }) => {
        if (key) {
          const relationshipType = linkEntry && linkEntry[key] && linkEntry[key].RelationshipType
          accumulateRelationshipTypes({ ob, relationshipType })
        }
      }
    })
  }

  ns.generateBrandReason = ({ linkTable, subject, key }) => {
    // the film reasons, reduce to 1 per film
    return ns.generateObMatches({
      linkTable,
      target: 'Brand',
      subject,
      matchType: 'brand',
      matchField: 'name',
      accumulate: ({ ob, linkEntry }) => {
        if (key) {
          const relationshipType = linkEntry && linkEntry[key] && linkEntry[key].RelationshipType
          accumulateRelationshipTypes({ ob, relationshipType })
        }
      }
    })
  }

  ns.generatePersonReason = ({ linkTable, subject }) => {
    // the film reasons, reduce to 1 per film
    return ns.generateObMatches({
      linkTable,
      target: 'Person',
      subject,
      matchType: 'person',
      matchField: 'lastName',
      accumulate: ({ ob, linkEntry }) => {
        standardizeAwardPageItems({ ob, linkEntry })
        accumulateProfessions({ ob, linkEntry })
      }
    })
  }

  ns.getDateFromRunID = ({ runID, base = 32, size = 9 }) => runID ? new Date(parseInt(runID.slice(0, size), base)) : ''
  ns.getRunID = () => new Date().getTime().toString(32)

  ns.generateCompanyReason = ({ linkTable, subject, key }) => {
    // the film reasons, reduce to 1 per film
    return ns.generateObMatches({
      linkTable,
      target: 'Company',
      subject,
      matchType: 'company',
      matchField: 'id',
      accumulate: ({ ob, linkEntry }) => {
        accumulateProfessions({ ob, linkEntry })
        accumulateCompanyTypes({ ob, companyType: linkEntry.CompanyType })
        if (key) {
          const relationshipType = linkEntry && linkEntry[key] && linkEntry[key].RelationshipType
          accumulateRelationshipTypes({ ob, relationshipType })
        }
      }
    })
  }
  ns.makeSearcherFromProduct = (product) => {
    if (!product) return null
    const searcher = {
      itemType: 'product',
      itemID: product.id,
      itemName: product.name
    }
    // the film reason
    const productReason = [{
      match: searcher.itemName,
      matchType: searcher.itemType,
      matchID: product.id,
      productID: product.id,
      Product: product
    }]
    // this is used later to quickly find the 'master' reason
    searcher.itemReason = productReason[0]

    // the film reasons, reduce to 1 per film
    const filmReason = ns.generateFilmReason({ linkTable: product.ProductToFilms, subject: product })

    // the product reasons, reduce to 1 per product
    const brandReason = ns.generateBrandReason({ linkTable: product.ProductToBrands, subject: product })

    // the product reasons, reduce to 1 per product
    const companyReason = ns.generateCompanyReason({ linkTable: product.ProductToCompanies, subject: product, key: 'CompanyToProductRelationshipType' })

    searcher.MatchReasons = [].concat.call(
      productReason,
      sortByMatch(filmReason),
      sortByMatch(companyReason),
      sortByMatch(brandReason)
    )
    return searcher
  }
  ns.makeSearcherFromBrand = (brand) => {
    if (!brand) return null
    const searcher = {
      itemType: 'brand',
      itemID: brand.id,
      itemName: brand.name
    }
    // the film reason
    const brandReason = [{
      match: searcher.itemName,
      matchType: searcher.itemType,
      matchID: brand.id,
      brandID: brand.id,
      Brand: brand
    }]
    // this is used later to quickly find the 'master' reason
    searcher.itemReason = brandReason[0]

    // the film reasons, reduce to 1 per film
    const filmReason = ns.generateFilmReason({ linkTable: brand.BrandToFilms, subject: brand })

    // the product reasons, reduce to 1 per product
    const productReason = ns.generateProductReason({ linkTable: brand.BrandToProducts, subject: brand })

    // the product reasons, reduce to 1 per product
    const companyReason = ns.generateCompanyReason({ linkTable: brand.BrandToCompanies, subject: brand, key: 'CompanyToBrandRelationshipType' })

    searcher.MatchReasons = [].concat.call(
      brandReason,
      sortByMatch(filmReason),
      sortByMatch(companyReason),
      sortByMatch(productReason)
    )
    return searcher
  }
  ns.makeSearcherFromPerson = (person) => {
    if (!person) return null

    const searcher = {
      itemType: 'person',
      itemID: person.id,
      itemName: person.lastName
    }
    // the person reason
    const personReason = [{
      match: searcher.itemName,
      matchType: searcher.itemType,
      matchID: person.id,
      personID: person.id,
      Person: person
    }]
    // this is used later to quickly find the 'master' reason
    searcher.itemReason = personReason[0]

    // the film reasons, reduce to 1 per film
    const filmReason = ns.generateFilmReason({ linkTable: person.PersonToFilms, subject: person })

    // the company reasons, reduce to 1 per company
    const companyReason = ns.generateCompanyReason({ linkTable: person.PersonToCompanies, subject: person })

    // award matches
    const awardReason = ns.generateAwardReason({ linkTable: person.PersonToAwards, subject: person })

    searcher.MatchReasons = [].concat.call(
      personReason,
      sortByMatch(filmReason),
      sortByProfession(companyReason),
      sortByAward(awardReason)
    )
    return searcher
  }
  // this one is rather different in nature from the others
  // as the awardpage query is rather more complex
  // and it has already done some of the accumulation usually required
  // the objective is the same though - to end up with it formatted as a set of reasons
  ns.makeSearcherFromAward = (award) => {
    if (!award) return null

    // use the id of the root award parent
    const searcher = {
      itemType: 'award',
      itemID: award.id,
      itemName: award.AwardLocalized && award.AwardLocalized.name
    }
    // generate a master award reason
    const awardReason = [{
      match: searcher.itemName,
      matchType: searcher.itemType,
      matchID: award.id,
      awardID: award.id,
      Award: award
    }]
    // this is used later to quickly find the 'master' reason
    searcher.itemReason = awardReason[0]

    // a reason for each film getting an award
    const filmReason = ns.generateFilmReason({ linkTable: award.ViewAwardFilms, subject: award })

    // and for each person getting an award
    const personReason = ns.generatePersonReason({ linkTable: award.ViewAwardPeople, subject: award })

    // sort an combine them all
    searcher.MatchReasons = [].concat.call(
      awardReason,
      sortByMatch(personReason),
      sortByMatch(filmReason)
    )
    return searcher
  }
  ns.makeSearcherFromFilm = (film) => {
    if (!film) return null
    const searcher = {
      itemType: 'film',
      itemID: film.id,
      itemName: film.filmName
    }
    // the film reason
    const filmReason = [{
      match: searcher.itemName,
      matchType: searcher.itemType,
      matchID: film.id,
      filmID: film.id,
      Film: film
    }]
    // this is used later to quickly find the 'master' reason
    searcher.itemReason = filmReason[0]

    // the person reasons, reduce to 1 per individual, and accumulate professions
    const personReason = ns.generatePersonReason({ linkTable: film.FilmToPeople, subject: film })

    // brand matches
    const brandReason = ns.generateBrandReason({ linkTable: film.FilmToBrands, subject: film })

    // product matches
    const productReason = ns.generateProductReason({ linkTable: film.FilmToProducts, subject: film })

    // company matches
    const companyReason = ns.generateCompanyReason({ linkTable: film.FilmToCompanies, subject: film })

    // award matches
    const awardReason = ns.generateAwardReason({ linkTable: film.FilmToAwards, subject: film })

    searcher.MatchReasons = [].concat.call(
      filmReason,
      sortByMatch(productReason),
      sortByMatch(brandReason),
      sortByProfession(personReason),
      sortByCompanyType(companyReason),
      sortByAward(awardReason)
    )
    return searcher
  }
  ns.makeSearcherFromCompany = (company) => {
    if (!company) return null

    const searcher = {
      itemType: 'company',
      itemID: company.id,
      itemName: company.id
    }
    // the person reason
    const companyReason = [{
      match: searcher.itemName,
      matchType: searcher.itemType,
      matchID: company.id,
      companyID: company.id,
      Company: company
    }]
    // this is used later to quickly find the 'master' reason
    searcher.itemReason = companyReason[0]

    // the film reasons, reduce to 1 per film
    const filmReason = ns.generateFilmReason({ linkTable: company.CompanyToFilms, subject: company })
    const personReason = ns.generatePersonReason({ linkTable: company.CompanyToPeople, subject: company })
    const brandReason = ns.generateBrandReason({ linkTable: company.CompanyToBrands, subject: company, key: 'CompanyToProductRelationshipType' })
    const productReason = ns.generateProductReason({ linkTable: company.CompanyToProducts, subject: company, key: 'CompanyToProductRelationshipType' })

    searcher.MatchReasons = [].concat.call(
      companyReason,
      sortByProfession(personReason),
      sortByCompanyType(filmReason),
      productReason,
      brandReason
    )
    return searcher
  }
  // this will be handy for reusing reason routing
  ns.makeSearcherFilmFromVimeoSearcher = (vimeoSearcher) => {
    if (!vimeoSearcher) return null
    if (vimeoSearcher.matchType !== 'vimeofilm') {
      console.log(`expected videofilm but got ${vimeoSearcher.matchType}`, vimeoSearcher)
    }

    const film = {
      ...vimeoSearcher.VimeoUserVideo.FilmMastersFromVimeoID[0].Film,
      FilmMasters: [...vimeoSearcher.VimeoUserVideo.FilmMastersFromVimeoID]
    }

    const searcher = {
      itemType: 'film',
      itemID: film.id,
      itemName: film.filmName
    }
    // the film reason
    const filmReason = [{
      match: vimeoSearcher.match,
      matchType: 'film',
      matchID: film.id,
      filmID: film.id,
      Film: film
    }]

    // this is used later to quickly find the 'master' reason
    searcher.itemReason = filmReason[0]

    searcher.MatchReasons = filmReason
    return searcher
  }
  // this is to make results from vimeo user videos look a little like reasons
  ns.makeSearcherFromVimeoUser = (vimeoUserInfo) => {
    if (!vimeoUserInfo) return null
    const searcher = {
      itemType: 'vimeouser',
      itemID: vimeoUserInfo.id,
      itemName: vimeoUserInfo.uri
    }
    const vimeoReasons = [{
      match: searcher.itemName,
      matchType: searcher.itemType,
      matchID: vimeoUserInfo.id,
      vimeoUserID: vimeoUserInfo.id,
      VimeoUser: vimeoUserInfo
    }]

    searcher.itemReason = vimeoReasons[0]
    searcher.MatchReasons = [].concat.call(
      vimeoReasons
    )
    return searcher
  }

  // this is to make results from vimeo look a little like reasons
  ns.combineSearcherVimeoUserVideos = ({ userItem, userVideosInfo }) => {
    if (!userVideosInfo || !userItem) return null
    // deep copy
    const userVideoItem = JSON.parse(JSON.stringify(userItem))

    // work out the reasons
    const vimeoUservideosReasons = userVideosInfo.map(f => {
      return {
        match: f.uri,
        matchID: f.id,
        matchType: 'vimeofilm',
        vimeoUserID: userItem.vimeoUserID,
        vimeoVideoID: f.id,
        VimeoUserVideo: f
      }
    })

    // add the video reason to the user reason
    userVideoItem.MatchReasons = [].concat.call(
      userItem.MatchReasons,
      vimeoUservideosReasons
    )
    return userVideoItem
  }

  return ns
})({})
